import { Alert, Avatar, Badge, Button, Card, Spinner } from '@parsina/framework';
import { C, soft } from '@parsina/framework/tokens';

import { useTranslation } from '../../i18n';
import { STACK } from '../../constants/density';

/**
 * One membership waiting for a decision — an invitation to accept, or a request
 * to withdraw.
 *
 * Built from the kit rather than beside it. The previous version declared its
 * own `Card`, its own `Heading` and its own role pill, and the pill computed
 * its tint by string concatenation — `` `${r.color}22` `` — which is the exact
 * trap `packages/DESIGN.md` names: the moment a role colour is a themed
 * `var(...)` rather than a literal hex, that expression produces
 * `var(--x)22`, which is not a colour at all. `soft()` emits `color-mix()` for
 * a variable and `rgba()` for a hex, so it is right for both.
 *
 * ── The role list is a consent surface ──────────────────────────────────────
 *
 * A person accepting a membership is agreeing to something. A role NAME alone
 * is not consent, it is a shrug — so the proposed roles are rendered from the
 * same labels the role editor uses, and a membership with no roles yet says so
 * as a warning rather than as quiet grey text. «هنوز نقشی مشخص نشده» is a real
 * thing to know before pressing accept.
 */
export default function MembershipCard({
  membership,
  busy,
  onAccept,
  onReject,
  onCancel,
  disabled,
  variant = 'invited',
}) {
  const { t } = useTranslation();
  const m = membership;
  const roles = m.roles || [];
  const isRequest = variant === 'requested';

  const meta = [
    m.city,
    !isRequest && m.invitedBy ? t('clinic.invitations.invitedBy', { name: m.invitedBy }) : null,
  ].filter(Boolean);

  return (
    <Card
      data-testid={`membership-${m.clinicId}`}
      style={{ padding: 16, marginBottom: STACK.regular }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* The monogram is decoration; the clinic NAME is the accessible name,
            so the avatar must not be read out as a second, meaningless label. */}
        <span aria-hidden="true" style={{ flexShrink: 0 }}>
          <Avatar name={m.clinicName} size={38} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* <bdi> because a clinic name may be Latin inside a Persian page —
              «Dr Smith Clinic» beside a «·» separator flips the whole line
              without it. */}
          <div
            style={{
              fontSize: '0.90625rem', fontWeight: 800, color: C.textPrimary,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
            title={m.clinicName}
          >
            <bdi>{m.clinicName}</bdi>
          </div>
          {isRequest ? (
            <div style={{ fontSize: '0.75rem', color: C.textSecondary, marginTop: 2 }}>
              {t('clinic.context.blocked.REQUESTED')}
            </div>
          ) : meta.length ? (
            <div style={{ fontSize: '0.75rem', color: C.textSecondary, marginTop: 2 }}>
              {meta.join(' · ')}
            </div>
          ) : null}

          {!isRequest ? (
            roles.length ? (
              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {roles.map((r) => (
                  <Badge
                    key={r.id}
                    style={r.color
                      ? { background: soft(r.color, 0.15), color: r.color }
                      : undefined}
                  >
                    {r.nameFa}
                  </Badge>
                ))}
              </div>
            ) : (
              <Alert variant="warning" style={{ marginTop: 8 }}>
                {t('clinic.invitations.noRoleYet')}
              </Alert>
            )
          ) : null}
        </div>
        {isRequest ? (
          <Button
            variant="ghost"
            onClick={() => onCancel(m)}
            disabled={busy || disabled}
            aria-busy={busy || undefined}
            data-testid={`cancel-${m.clinicId}`}
          >
            {t('clinic.invitations.cancelRequest')}
          </Button>
        ) : null}
      </div>

      {!isRequest ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          <Button
            onClick={() => onAccept(m)}
            disabled={busy || disabled}
            aria-busy={busy || undefined}
            data-testid={`accept-${m.clinicId}`}
          >
            {busy ? <Spinner size={14} color="#fff" label={t('common.state.loading')} /> : null}
            {busy ? t('clinic.invitations.accepting') : t('clinic.invitations.accept')}
          </Button>
          <Button
            variant="outline"
            onClick={() => onReject(m)}
            disabled={busy || disabled}
            data-testid={`reject-${m.clinicId}`}
          >
            {t('clinic.invitations.reject')}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
